// Unified device detail: identity + presence + this device's manuals on one
// surface, with the forensic NMEA fields behind a "Technical details"
// disclosure. Ends the old Overview / Devices / Details ping-pong.

import { Fragment, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { presenceDotClass, useBoatChrome } from '../components/app/boatChrome';
import { playbackNowMs } from '../lib/playback';
import { AnalyzerIcon, InfoCircleIcon } from '../components/app/icons';
import { deviceChannelRows } from '../lib/deviceChannels';
import { formatBytes, seenStatus } from '../lib/deviceSeen';
import { findRicherTwin } from '../lib/deviceTwins';
import { useDocumentTitle } from '../lib/useDocumentTitle';

export function DeviceDetail() {
  const { boatId, boat, devices } = useBoatChrome();
  const { deviceId = '' } = useParams<{ deviceId: string }>();
  const decoded = decodeURIComponent(deviceId);

  // Playback-aware: on a replaying boat the device stamps are recording-time,
  // so freshness must be judged on the recording's clock (lib/playback.ts).
  const [now, setNow] = useState(() => playbackNowMs(boat?.simTime));
  const simTime = boat?.simTime ?? null;
  useEffect(() => {
    setNow(playbackNowMs(simTime));
    const id = setInterval(() => setNow(playbackNowMs(simTime)), 30_000);
    return () => clearInterval(id);
  }, [simTime]);

  const inventory = devices.state.kind === 'ready' ? devices.state.devices : [];
  const device = inventory.find((d) => d.deviceId === decoded);

  useDocumentTitle(device ? `${device.displayName} — Device` : 'Device — Neomaris');

  if (devices.state.kind === 'loading') {
    return <p className="muted">Loading…</p>;
  }
  if (!device) {
    return (
      <section>
        <div className="page-head">
          <h1>Device not found</h1>
          <p className="page-sub">It may have been removed from this boat.</p>
        </div>
        <p>
          <Link to={`/dashboard/boats/${boatId}/devices`}>← Back to devices</Link>
        </p>
      </section>
    );
  }

  const seen = seenStatus(device.lastSeen, now);
  const manuals = device.manuals ?? [];
  const channels = deviceChannelRows(device);
  // The same physical unit seen through another protocol, when that row is the
  // one carrying the channel names. Null for nearly every device, and always
  // null once this device has channels of its own.
  const richerTwin = findRicherTwin(device, inventory);
  const cfg = device.configurationInfo;
  const quiet = seen.state === 'cold' || seen.state === 'never';
  const meta = [device.manufacturer?.name, device.productInfo?.modelId, device.deviceFunction?.name]
    .filter(Boolean)
    .join(' · ');

  return (
    <section data-testid="device-detail">
      <Link className="detail-back" to={`/dashboard/boats/${boatId}/devices`}>
        ← Back
      </Link>
      <div className="detail-cols">
        <div>
          <div className="panel">
            <div className="identity-name">
              <span
                className={`boat-status-dot boat-status-dot--${presenceDotClass(seen.state)}`}
                title={seen.status}
                aria-label={seen.status}
              />
              <h2>{device.displayName}</h2>
            </div>
            {meta ? (
              <p className="muted" style={{ margin: '0 0 8px' }}>
                {meta}
              </p>
            ) : null}
            <span className="seen">
              <span className={`seen-dot seen-dot--${seen.state}`} title={seen.status} />
              {seen.label}
            </span>
            <Link
              className="toolkit-button toolkit-device-analyzer-link"
              to={`/dashboard/boats/${boatId}/toolkit?tab=configure&device=${encodeURIComponent(device.deviceId)}`}
            >
              <AnalyzerIcon /> Analyze and configure
            </Link>
          </div>

          <details className="section-collapse panel" style={{ marginTop: 14, paddingTop: 6 }}>
            <summary>Technical details</summary>
            <dl
              style={{
                display: 'grid',
                gridTemplateColumns: 'max-content 1fr',
                gap: '6px 16px',
                fontSize: 13,
                margin: '8px 0 0',
              }}
            >
              {device.productInfo?.serialCode ? (
                <>
                  <dt className="faint">Serial</dt>
                  <dd>{device.productInfo.serialCode}</dd>
                </>
              ) : null}
              {device.productInfo?.softwareVersion ? (
                <>
                  <dt className="faint">Software</dt>
                  <dd>{device.productInfo.softwareVersion}</dd>
                </>
              ) : null}
              {device.deviceClass?.name || device.deviceFunction?.name ? (
                <>
                  <dt className="faint">Class / Function</dt>
                  <dd>
                    {[device.deviceClass?.name, device.deviceFunction?.name]
                      .filter(Boolean)
                      .join(' · ')}
                  </dd>
                </>
              ) : null}
              {cfg?.installationDescription1 ? (
                <>
                  <dt className="faint">Install 1</dt>
                  <dd>{cfg.installationDescription1}</dd>
                </>
              ) : null}
              {cfg?.installationDescription2 ? (
                <>
                  <dt className="faint">Install 2</dt>
                  <dd>{cfg.installationDescription2}</dd>
                </>
              ) : null}
              {device.transmitPgns && device.transmitPgns.length > 0 ? (
                <>
                  <dt className="faint">Transmits PGNs</dt>
                  <dd className="mono">{device.transmitPgns.join(', ')}</dd>
                </>
              ) : null}
              {device.receivePgns && device.receivePgns.length > 0 ? (
                <>
                  <dt className="faint">Receives PGNs</dt>
                  <dd className="mono">{device.receivePgns.join(', ')}</dd>
                </>
              ) : null}
              {device.canName ? (
                <>
                  <dt className="faint">CAN NAME</dt>
                  <dd className="mono">{device.canName}</dd>
                </>
              ) : null}
              <dt className="faint">Device ID</dt>
              <dd className="mono">{device.deviceId}</dd>
              <dt className="faint">First seen</dt>
              <dd>{new Date(device.firstSeen).toLocaleString()}</dd>
            </dl>
          </details>
        </div>

        <div>
          {/* Channels sits above Manuals: on a RIM100/SIM100 the channel list is
              the most useful thing on the page. It renders only when the device
              reported channels, so it never pushes Manuals down on the devices
              (nearly all of them) that have no concept of channels. */}
          {channels.length > 0 ? (
            <div
              className="panel"
              style={{ marginBottom: 14 }}
              data-testid={`device-channels-${device.deviceId}`}
            >
              <h3>Channels</h3>
              {/* Same dl grid as Technical details; margin 0 because the .panel
                  h3 already owns the 14px gap (as in the Manuals card). */}
              <dl
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'max-content 1fr',
                  gap: '6px 16px',
                  fontSize: 13,
                  margin: 0,
                }}
              >
                {channels.map((c) => (
                  <Fragment key={c.channel}>
                    <dt className="faint">Channel {c.channel}</dt>
                    <dd>{c.label}</dd>
                  </Fragment>
                ))}
              </dl>
            </div>
          ) : null}

          {/* Where Channels would have been. A boat running both a native N2K
              gateway and Signal K lists one physical device twice, and only one
              of the two rows reports the channel names — so this row is not
              broken, it is the other half of a pair. One quiet line of
              orientation, not a warning: the rows stay deliberately separate
              (core `wireDeviceId`), we just name the sibling and link to it.
              Renders nothing at all when there is no richer twin. */}
          {channels.length === 0 && richerTwin ? (
            <p
              className="muted"
              style={{ margin: '0 0 14px', fontSize: 13 }}
              data-testid={`device-twin-${device.deviceId}`}
            >
              Same device as{' '}
              <Link
                to={`/dashboard/boats/${boatId}/devices/${encodeURIComponent(richerTwin.deviceId)}`}
                data-testid={`device-twin-link-${device.deviceId}`}
              >
                {richerTwin.displayName}
              </Link>{' '}
              — channel names are reported there.
            </p>
          ) : null}

          <div className="panel" data-testid={`device-manuals-${device.deviceId}`}>
            <h3>Manuals</h3>
            {manuals.length === 0 ? (
              <div className="empty-card" style={{ padding: 20 }}>
                <p>No device manual on file yet for this model.</p>
                <p className="muted">Manuals stay available whether or not the device is online.</p>
              </div>
            ) : (
              manuals.map((m) => {
                const size = m.sizeBytes != null ? formatBytes(m.sizeBytes) : '';
                const spec = [m.revision, m.language, size].filter(Boolean).join(' · ');
                return (
                  <div className="detail-manual-card" key={m.id} data-testid={`manual-row-${m.id}`}>
                    <div className="dmc-top">
                      <div style={{ minWidth: 0 }}>
                        <div className="dmc-title">{m.title}</div>
                        {spec ? <div className="dmc-spec">{spec}</div> : null}
                      </div>
                    </div>
                    <div className="dmc-actions">
                      <a
                        className="btn-open-sm"
                        style={{ flex: 1 }}
                        href={m.downloadUrl}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`open-manual-${device.deviceId}`}
                      >
                        Open manual
                      </a>
                    </div>
                  </div>
                );
              })
            )}
            {quiet && manuals.length > 0 ? (
              <div className="honest-note">
                <InfoCircleIcon />
                This device is quiet right now, but its manual is still here — presence never gates
                the manual.
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
